import React, {
  useState, useEffect, useContext,
} from 'react';
import axios from 'axios';
import { baseUrl } from 'Api';
import { AuthContext } from 'Context';
import PaymentModalV2 from './PaymentModalV2';
import styles from './createUserV2.module.css';

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// "Finish Setup" popup for an affiliate who abandoned the Full Service flow after
// step 1 (user created, still on a free plan). Reproduces the creation experience
// in a popup — no full page: pick a plan here, then hand off to PaymentModalV2 for
// payment against the EXISTING user. PaymentModalV2 is self-contained via props
// (userId from `user`, plan from `plan`), so no create step / localStorage priming
// is needed.
const FinishSetupModal = ({
  open, onClose, user, onPaid,
}) => {
  const { getToken } = useContext(AuthContext);
  const myToken = getToken();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [payPlan, setPayPlan] = useState(null); // chosen plan → payment step

  useEffect(() => {
    if (!open) return undefined;
    setSelectedPlanId(null);
    setPayPlan(null);
    setLoading(true);
    let active = true;
    axios
      .post(`${baseUrl}/getAllActivePlans`, { for_affiliate: true, token: myToken })
      .then((res) => {
        if (active) setPlans(res.data?.success && Array.isArray(res.data.data) ? res.data.data : []);
      })
      .catch(() => { if (active) setPlans([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  if (!open) return null;

  // Payment step — reuse the create-flow popup for the chosen plan + existing user.
  if (payPlan) {
    return (
      <PaymentModalV2
        open
        plan={payPlan}
        user={user}
        onClose={() => { setPayPlan(null); onClose(); }}
        onDoneClose={() => { setPayPlan(null); onClose(); }}
        onPaid={onPaid}
      />
    );
  }

  const selected = plans.find((p) => p.id === selectedPlanId);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={styles.overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.mHead}>
          <h3 className={styles.mTitle}>Choose a plan</h3>
          <button type="button" className={styles.mClose} onClick={onClose}>×</button>
        </div>
        <p className={styles.mSub}>
          {`Assign a plan to ${user?.firstname || user?.email || 'this user'} to finish setup.`}
        </p>

        {loading ? (
          <p className={styles.planState}>Loading plans…</p>
        ) : !plans.length ? (
          <p className={styles.planState}>No plans available.</p>
        ) : (
          <div className={styles.planList}>
            {plans.map((plan) => {
              const full = Number(plan.amount);
              const aff = plan.affiliate;
              const eff = aff ? Number(aff.effective_amount) : full;
              const isFree = eff === 0;
              const discounted = eff < full;
              const isSel = selectedPlanId === plan.id;
              let off = '';
              if (aff?.discount && !isFree) {
                const d = aff.discount;
                if (d.type === 'percentage' && d.value) off = `${d.value}% off`;
                else if (d.value) off = `$${(Number(d.value) / 100).toFixed(2)} off`;
              }
              return (
                <div
                  key={plan.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {}}
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`${styles.planOption} ${isSel ? styles.planSelected : ''}`}
                >
                  <span className={styles.radio} />
                  <div className={styles.planInfo}>
                    <span className={styles.planName}>{plan.product_name}</span>
                    {plan.price_description && (
                      <span className={styles.planSub}>{plan.price_description}</span>
                    )}
                  </div>
                  <div className={styles.planPrice}>
                    {isFree ? (
                      <span className={styles.freeBadge}>Included</span>
                    ) : (
                      <div className={styles.priceRow}>
                        <span className={styles.eff}>{money(eff)}</span>
                        {discounted && <span className={styles.full}>{money(full)}</span>}
                      </div>
                    )}
                    {off && <span className={styles.offBadge}>{off}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className={styles.mPay}
          disabled={!selected}
          onClick={() => setPayPlan(selected)}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default FinishSetupModal;
